/**
 * FIELD CAPTURE — phone first.
 *
 * A worker should not have to write a report. They record what happened, and
 * the reporting is composed from those records later.
 *
 * Two things this screen holds apart, because the contract does:
 *
 *   RECORD WHAT HAPPENED  — always stored. Shape is validated; readiness,
 *                           gates and authority are not. It cannot be refused.
 *   START WORK            — a governed request. Confirmed, separately
 *                           permissioned, and it may refuse with reasons.
 *
 * And the rule that matters most after a submission: a refused projection
 * never erases the observation. When the machine declines to move canonical
 * state, BOTH stand — the field said one thing, the model says another, and
 * that contradiction is shown rather than resolved away.
 *
 * TWO DEFECTS THIS SCREEN CARRIED, BOTH GREEN THE WHOLE TIME
 *
 * 1. IT ASKED FOR THE WRONG FIELDS. Only `spec.requires` was rendered, and
 *    every projection ALSO refuses without its own date key (`startedOn`,
 *    `finishedOn`, `logDate`, `deliveredOn`, `receivedOn`, `verifiedOn`). So
 *    "Work started" → Record produced `400 startedOn is required` for a field
 *    nobody had been asked for. The form now renders `requiredFields()`, the
 *    union, date first.
 *
 * 2. IT READ THE WRONG SHAPE. The POST returns an ENVELOPE —
 *    `{contractVersion, observation, created, unknowns}` — and this component
 *    handed that envelope to `presentObservation`, which reads `.projection`
 *    off an observation ROW. Against the real backend `.projection` was
 *    therefore always undefined, every submission rendered "Recorded", and
 *    the REFUSED state — the entire point of the screen — could not appear.
 *    It passed because the browser fixture returned a bare row instead of the
 *    envelope, so the mock and the component were wrong in the same
 *    direction. Both are corrected, and `presentSubmissionOutcome` reads the
 *    envelope.
 */
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  OBSERVATION_TYPES,
  START_WORK_COMMAND,
  observationTypesFor,
  missingRequiredFields,
  requiredFields,
  optionalFields,
  payloadFieldDisplay,
  describeMissing,
  buildObservationRequest,
  buildIdempotencyKey,
  presentFieldRefusal,
} from "./fieldPresentation";
import { presentSubmissionOutcome } from "../materials/materialPresentation";
import {
  ClipboardCheck,
  Play,
  TriangleAlert,
  CheckCircle2,
} from "lucide-react";

/**
 * One payload field, asked for in the form rather than discovered in a 400,
 * and named the way the trade names it rather than by its wire key.
 */
const PayloadField = ({ field, value, onChange, optional = false }) => {
  const display = payloadFieldDisplay(field);
  const help = optional ? display.optionalHelp || display.help : display.help;

  if (display.kind === "boolean") {
    return (
      <div className="space-y-1">
        <Label className="text-[12px]">{display.label}</Label>
        <div className="flex gap-2">
          {/* A short quantity is a real answer, not an empty field. */}
          <Button
            type="button"
            size="sm"
            variant={value === true ? "default" : "outline"}
            onClick={() => onChange(true)}
            data-testid="qty-sufficient-yes"
          >
            Yes
          </Button>
          <Button
            type="button"
            size="sm"
            variant={value === false ? "default" : "outline"}
            onClick={() => onChange(false)}
            data-testid="qty-sufficient-no"
          >
            No — it is short
          </Button>
        </div>
        {help && <p className="text-[10px] text-muted-foreground">{help}</p>}
      </div>
    );
  }

  const type =
    display.kind === "date"
      ? "date"
      : display.kind === "number"
        ? "number"
        : "text";
  return (
    <div className="space-y-1">
      <Label className="text-[12px]" htmlFor={`field-${field}`}>
        {display.label}
        {optional && (
          <span className="ml-1 font-normal text-muted-foreground">
            (if you have it)
          </span>
        )}
      </Label>
      <Input
        id={`field-${field}`}
        data-testid={`field-${field}`}
        type={type}
        inputMode={display.kind === "number" ? "numeric" : undefined}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
      {help && <p className="text-[10px] text-muted-foreground">{help}</p>}
    </div>
  );
};

/**
 * WHAT THE BACKEND DID WITH WHAT YOU RECORDED — four separate facts.
 *
 * STORED is not CREATED is not PROJECTED, and what the machine could NOT
 * establish is its own line. A refusal is never worded as a failure to save,
 * because the record is safe either way.
 */
const Outcome = ({ result }) => {
  const outcome = presentSubmissionOutcome(result);

  if (!outcome.readable) {
    return (
      <Alert className="border-border bg-muted/20" data-testid="field-outcome">
        <TriangleAlert className="h-4 w-4" />
        <AlertDescription className="space-y-1">
          <p className="text-[12px] font-medium">{outcome.display.headline}</p>
          <p className="text-[11px] text-muted-foreground">
            {outcome.display.lead}
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  const { display, refused, projectionDetail, unknowns, created } = outcome;
  return (
    <Alert
      className={
        refused
          ? "border-amber-500/40 bg-amber-500/10"
          : "border-border bg-muted/20"
      }
      data-testid="field-outcome"
      data-outcome={outcome.outcome}
      data-projection={outcome.projectionState}
    >
      {refused ? (
        <TriangleAlert className="h-4 w-4" />
      ) : (
        <CheckCircle2 className="h-4 w-4" />
      )}
      <AlertDescription className="space-y-1">
        {/* Announced, not only drawn: a field user may never see this land. */}
        <p className="text-[12px] font-medium" role="status" aria-live="polite">
          {display.headline}
        </p>
        <p className="text-[11px] text-muted-foreground">{display.lead}</p>

        {/* The machine's own reasons at recording time. Never paraphrased. */}
        {projectionDetail && (
          <p
            className="text-[11px] border-l-2 border-border pl-2"
            data-testid="projection-detail"
          >
            {projectionDetail}
          </p>
        )}

        {/*
          What OMNΛS could NOT establish while projecting. Previously dropped
          on the floor — which let "the receiving record names no quantity" go
          unsaid, and a person believe a quantity had been captured.
        */}
        {unknowns.length > 0 && (
          <ul className="space-y-0.5 pt-0.5" data-testid="outcome-unknowns">
            {unknowns.map((line) => (
              <li
                key={line}
                className="text-[11px] text-muted-foreground flex gap-1.5"
              >
                <span aria-hidden="true">·</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
          {/* Storage is the one thing that is always true on a 2xx. */}
          <Badge
            variant="outline"
            className="text-[10px] font-normal"
            data-testid="stored-badge"
          >
            Stored
          </Badge>
          {!created && (
            <Badge variant="outline" className="text-[10px] font-normal">
              Not duplicated
            </Badge>
          )}
          <Badge variant="outline" className="text-[10px] font-normal">
            HUMAN_ASSERTED
          </Badge>
        </div>
      </AlertDescription>
    </Alert>
  );
};

const FieldCaptureSheet = ({
  open,
  onOpenChange,
  target,
  targetId,
  targetName,
  onRecord,
  onStartWork,
}) => {
  const [type, setType] = useState(null);
  const [payload, setPayload] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [confirmStart, setConfirmStart] = useState(false);
  // Stable for one attempt, unique across attempts.
  const [nonce] = useState(() => Math.random().toString(36).slice(2, 10));

  const types = observationTypesFor(target);
  const spec = type ? OBSERVATION_TYPES[type] : null;
  const missing = type ? missingRequiredFields(type, payload) : [];

  const reset = () => {
    setType(null);
    setPayload({});
    setResult(null);
    setError(null);
    setConfirmStart(false);
  };

  const submit = async () => {
    if (!type || missing.length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const body = buildObservationRequest({
        observationType: type,
        observedAt: new Date().toISOString(),
        payload,
        idempotencyKey: buildIdempotencyKey(targetId, type, nonce),
      });
      setResult(await onRecord(body));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await onStartWork({ confirmed: true }));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const refusal = error ? presentFieldRefusal(error) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="field-capture"
      >
        <DialogHeader>
          <DialogTitle className="text-base break-words">
            {targetName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {result ? (
            <>
              <Outcome result={result} />
              <Button
                variant="outline"
                className="w-full"
                onClick={reset}
                data-testid="record-another"
              >
                Record something else
              </Button>
            </>
          ) : (
            <>
              {refusal && (
                <Alert
                  className="border-amber-500/40 bg-amber-500/10"
                  data-testid="field-refusal"
                >
                  <TriangleAlert className="h-4 w-4" />
                  <AlertDescription>
                    <p
                      className="text-[12px] font-medium"
                      data-testid="refusal-title"
                    >
                      {refusal.title}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {refusal.detail}
                    </p>
                    {/* The runtime's own sentence, kept whole beside our
                        classification rather than replaced by it. */}
                    {refusal.serverMessage ? (
                      <p
                        className="text-[10px] text-muted-foreground leading-relaxed"
                        data-testid="refusal-server-message"
                      >
                        {refusal.serverMessage}
                      </p>
                    ) : null}
                    {refusal.caveat ? (
                      <p
                        className="text-[10px] text-muted-foreground leading-relaxed"
                        data-testid="refusal-caveat"
                      >
                        {refusal.caveat}
                      </p>
                    ) : null}
                  </AlertDescription>
                </Alert>
              )}

              {/* A recorded fact. Cannot be refused storage. */}
              <div className="space-y-1.5">
                <p className="text-[11px] font-medium flex items-center gap-1">
                  <ClipboardCheck className="w-3.5 h-3.5" aria-hidden="true" />
                  Record what happened
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Stored exactly as you say it. OMNΛS may decline to move the
                  project, and your record still stands.
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {types.map((key) => (
                    <Button
                      key={key}
                      size="sm"
                      variant={type === key ? "default" : "outline"}
                      className="justify-start text-[12px] h-auto py-2"
                      onClick={() => {
                        setType(key);
                        setPayload({});
                      }}
                      data-testid={`observe-${key}`}
                    >
                      {OBSERVATION_TYPES[key].label}
                    </Button>
                  ))}
                </div>
              </div>

              {spec && (
                <div className="space-y-2 border-t border-border/60 pt-2">
                  <p className="text-[12px]">{spec.prompt}</p>
                  {spec.note && (
                    <p className="text-[10px] text-muted-foreground italic">
                      {spec.note}
                    </p>
                  )}
                  {/* The union, date first. See defect 1 in the header. */}
                  {requiredFields(type).map((field) => (
                    <PayloadField
                      key={field}
                      field={field}
                      value={payload[field]}
                      onChange={(v) =>
                        setPayload((p) => ({ ...p, [field]: v }))
                      }
                    />
                  ))}
                  {optionalFields(type).length > 0 && (
                    <div
                      className="space-y-2 rounded-md border border-border/60 p-2"
                      data-testid="optional-fields"
                    >
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Only if you have it
                      </p>
                      {optionalFields(type).map((field) => (
                        <PayloadField
                          key={field}
                          field={field}
                          optional
                          value={payload[field]}
                          onChange={(v) =>
                            setPayload((p) => ({ ...p, [field]: v }))
                          }
                        />
                      ))}
                    </div>
                  )}
                  <Button
                    className="w-full"
                    disabled={busy || missing.length > 0}
                    onClick={submit}
                    data-testid="field-submit"
                  >
                    {busy ? "Recording…" : "Record"}
                  </Button>
                  {missing.length > 0 && (
                    <p
                      className="text-[10px] text-muted-foreground"
                      data-testid="missing-fields"
                    >
                      OMNΛS needs {describeMissing(missing)} before this can be
                      recorded.
                    </p>
                  )}
                </div>
              )}

              {/* The governed command, deliberately apart from the facts above. */}
              {target === "instance" && onStartWork && (
                <div className="space-y-1.5 border-t border-border/60 pt-2">
                  <p className="text-[11px] font-medium flex items-center gap-1 text-blue-400">
                    <Play className="w-3.5 h-3.5" aria-hidden="true" />
                    {START_WORK_COMMAND.label}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {START_WORK_COMMAND.meaning} This is a different act from
                    recording that work started.
                  </p>
                  {confirmStart ? (
                    <Button
                      className="w-full"
                      variant="secondary"
                      disabled={busy}
                      onClick={start}
                      data-testid="start-work-confirm"
                    >
                      Yes — request the start
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => setConfirmStart(true)}
                      data-testid="start-work"
                    >
                      {START_WORK_COMMAND.label}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FieldCaptureSheet;
